/**
 * AffiliatedOrgPicker — Phase 1.7.4 multi-add org picker for
 * SuggestResourceForm individual-mode "Organization(s)" field.
 *
 * Mirror of IndividualPicker's multi-add shape, inverted for orgs:
 * search existing orgs in the directory OR inline-create a new org
 * (the new org enters the parent suggested_resources payload as an
 * inline draft + creates on approve via the dual-creation branch in
 * applySuggestedResource).
 *
 * Entry shape:
 *   { kind: 'existing', provider_organization_id: string }
 *   { kind: 'inline',   draft: OrganizationPayload }
 *
 * The parent maps entries[] → suggested_resources.payload.affiliations[]
 * — the affiliations array's entries are extended to carry an optional
 * `inline` payload (server-side back-compat: legacy entries with only
 * provider_organization_id continue to apply unchanged).
 *
 * NOT to be confused with src/components/peace-hub-port/OrganizationPicker.jsx
 * — that picker is the IndividualDetail.jsx affiliations-editor flow
 * which routes inline-create through a separate suggested_resources
 * round-trip. This picker keeps inline-create local to the parent
 * payload (single-submit suggested_resources entry for the entire
 * individual + affiliated orgs).
 *
 * UX-13 S5 — TypeScript; the add/save/cancel actions compose <Button>
 * (retired rounded-full + text-white pills go with it). The ✕ remove
 * glyphs + option rows + the Edit-details text link stay native buttons
 * (not Button-class affordances), so the file stays consciously
 * allowlisted.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { SearchField } from '@/components/primitives/SearchField';
import { TextInput } from '@/components/primitives/TextInput';
import type { OrganizationSubmitPayload } from '@/lib/suggestedResources';
import { lookupsApi } from '@/lib/lookups';
import { CATEGORY_SLUG_TO_DISPLAY } from '@/lib/uiCategoryLabels';
import { formatCountyDisplay } from '@/lib/serviceAreas';
import { MultiSelectCheckbox } from '@/components/MultiSelectCheckbox';
import { MEDICAID_VALUES } from '@/lib/facetTaxonomy';
import { SelectField } from '@/components/primitives/SelectField';
import { AddressCheckChoices, AddressCheckPrompt, AddressFields, useAddressForm, type AddressFormParts } from '@/components/clientRecord/AddressesCard';
import { formatPostalParts, postalPartsOf } from '@/lib/address';

const LOOKUP_STALE_MS = 10 * 60 * 1000;

// The ONE facet source (facetTaxonomy header contract) — this list used
// to be a local duplicate and drifted risk; the 0129 label+value
// unification re-anchored it on the canonical module.
const MEDICAID_OPTIONS: ReadonlyArray<string> = MEDICAID_VALUES;

export type AffiliationEntry =
  | { kind: 'existing'; provider_organization_id: string }
  | { kind: 'inline'; draft: OrganizationSubmitPayload };

export interface AffiliatedOrgPickerProps {
  tenantSlug: string;
  entries: AffiliationEntry[];
  onChange: (entries: AffiliationEntry[]) => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  searchResults: Array<{ id: string; name: string }>;
  resources: Array<{ id: string; name: string }>;
}

export function AffiliatedOrgPicker({
  tenantSlug,
  entries,
  onChange,
  searchTerm,
  onSearchTermChange,
  searchResults,
  resources,
}: AffiliatedOrgPickerProps): ReactNode {
  const [isInlineOpen, setIsInlineOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const idToName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of resources) m.set(r.id, r.name);
    return m;
  }, [resources]);

  const addedExistingIds = useMemo(() => {
    const s = new Set<string>();
    for (const e of entries) {
      if (e.kind === 'existing') s.add(e.provider_organization_id);
    }
    return s;
  }, [entries]);

  const visibleSearchResults = useMemo(
    () => searchResults.filter((r) => !addedExistingIds.has(r.id)),
    [searchResults, addedExistingIds],
  );

  function addExisting(id: string) {
    onChange([...entries, { kind: 'existing', provider_organization_id: id }]);
    onSearchTermChange('');
  }

  function addInline(draft: OrganizationSubmitPayload) {
    onChange([...entries, { kind: 'inline', draft }]);
  }

  function updateInlineAt(idx: number, draft: OrganizationSubmitPayload) {
    const next = entries.slice();
    next[idx] = { kind: 'inline', draft };
    onChange(next);
  }

  function removeAt(idx: number) {
    onChange(entries.filter((_, i) => i !== idx));
  }

  return (
    <div>
      {/* Stacked entries — chips for existing, cards for inline. */}
      {entries.length > 0 && (
        <div className="mb-2 space-y-2">
          {entries.map((entry, idx) => {
            if (entry.kind === 'existing') {
              return (
                <ExistingOrgChip
                  key={`existing-${entry.provider_organization_id}`}
                  name={idToName.get(entry.provider_organization_id) ?? entry.provider_organization_id}
                  onRemove={() => removeAt(idx)}
                />
              );
            }
            return (
              <InlineOrgCard
                key={`inline-${idx}`}
                draft={entry.draft}
                onEdit={() => {
                  setEditingIdx(idx);
                  setIsInlineOpen(true);
                }}
                onRemove={() => removeAt(idx)}
              />
            );
          })}
        </div>
      )}

      {/* Persistent search-as-you-type input. */}
      <SearchField
        value={searchTerm}
        onChange={onSearchTermChange}
        placeholder="Search organizations"
        aria-label="Search organizations"
        testId="affiliated-org-picker-search"
      />
      {searchTerm && visibleSearchResults.length > 0 && (
        <ul className="mt-1 max-h-40 overflow-y-auto bg-white border border-sage-800/[8%] rounded-md text-sm">
          {visibleSearchResults.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addExisting(r.id)}
                className="block w-full text-left px-3 py-2 hover:bg-sage-50 cursor-pointer"
              >
                {r.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {searchTerm && visibleSearchResults.length === 0 && (
        <p className="mt-1 text-xs text-ink-soft">Add a new organization below.</p>
      )}

      {/* Persistent "+ Add new organization" button. */}
      <Button
        variant="secondary"
        size="sm"
        className="mt-2"
        icon={<span aria-hidden="true">+</span>}
        onClick={() => {
          setEditingIdx(null);
          setIsInlineOpen(true);
        }}
      >
        Add new organization
      </Button>

      {isInlineOpen && (
        <InlineOrgForm
          tenantSlug={tenantSlug}
          initial={(() => {
            // editingIdx is only ever set from an inline card; narrow the union.
            const e = editingIdx !== null ? entries[editingIdx] : null;
            return e && e.kind === 'inline' ? e.draft : null;
          })()}
          onSave={(draft) => {
            if (editingIdx !== null) {
              updateInlineAt(editingIdx, draft);
            } else {
              addInline(draft);
            }
            setIsInlineOpen(false);
            setEditingIdx(null);
            onSearchTermChange('');
          }}
          onCancel={() => {
            setIsInlineOpen(false);
            setEditingIdx(null);
          }}
        />
      )}
    </div>
  );
}

function ExistingOrgChip({ name, onRemove }: { name: string; onRemove: () => void }): ReactNode {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sage-100 text-sage-800 text-sm">
        <span className="font-semibold">{name}</span>
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${name}`}
        className="text-ink-soft hover:text-sage-800 text-sm"
      >
        ✕
      </button>
    </div>
  );
}

/** A draft's address as one line: its parts formatted, or legacy text. */
function draftAddressText(draft: OrganizationSubmitPayload): string {
  const parts = postalPartsOf(draft.address);
  if (parts !== null) return formatPostalParts(parts);
  return typeof draft.address === 'string' ? draft.address : '';
}

function InlineOrgCard({ draft, onEdit, onRemove }: { draft: OrganizationSubmitPayload; onEdit: () => void; onRemove: () => void }): ReactNode {
  return (
    <div className="border border-sage-200 bg-sage-50 rounded-md p-3">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-700 mb-0.5">
            New organization (will be created on approve)
          </p>
          <p className="text-sm font-semibold text-ink truncate">
            {draft.name}
          </p>
          {draft.category_slug && (
            <p className="text-xs text-ink-soft mt-0.5">
              {CATEGORY_SLUG_TO_DISPLAY[draft.category_slug] ?? draft.category_slug}
            </p>
          )}
          {(draft.phone || draftAddressText(draft)) && (
            <p className="text-xs text-ink-soft mt-0.5">
              {draft.phone}
              {draft.phone && draftAddressText(draft) && ' · '}
              {draftAddressText(draft)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove new organization ${draft.name}`}
          className="text-ink-soft hover:text-sage-800 text-sm shrink-0"
        >
          ✕
        </button>
      </div>
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-sage-700 hover:text-sage-900 underline"
        >
          Edit details
        </button>
      </div>
    </div>
  );
}

/**
 * Inline-create sub-form for an organization. Field set mirrors
 * SuggestResourceForm org-mode (name + category + service types +
 * specialties + counties + phone + phone_2 + fax + address + website +
 * medicaid + dementia + notes). The new org is implicitly linked to the
 * parent individual via the affiliation entry that wraps this draft.
 *
 */
function InlineOrgForm({ tenantSlug, initial, onSave, onCancel }: {
  tenantSlug: string;
  initial: OrganizationSubmitPayload | null;
  onSave: (draft: OrganizationSubmitPayload) => void;
  onCancel: () => void;
}): ReactNode {
  const i: Partial<OrganizationSubmitPayload> = initial ?? {};
  const [name, setName] = useState(i.name ?? '');
  const [categorySlug, setCategorySlug] = useState(i.category_slug ?? '');
  const [subcatSlugs, setSubcatSlugs] = useState<string[]>(i.subcategories ?? []);
  const [specSlugs, setSpecSlugs] = useState<string[]>(i.specialties ?? []);
  const [countySlugs, setCountySlugs] = useState<string[]>(i.counties ?? []);
  const [phone, setPhone] = useState(i.phone ?? '');
  const [phone2, setPhone2] = useState(i.phone_2 ?? '');
  const [fax, setFax] = useState(i.fax ?? '');
  // The address: the shared address form, a Directory address (a town
  // and a state at least); a street is checked before the save.
  const address = useAddressForm({
    existing: postalPartsOf(i.address),
    rule: 'directory',
    idPrefix: 'inline-org',
    done: false,
    saving: false,
    onSave: (parts) => finishSave(parts),
  });
  const [website, setWebsite] = useState(i.website ?? '');
  const [medicaid, setMedicaid] = useState(i.medicaid ?? '');
  const [dementiaUnit, setDementiaUnit] = useState(i.dementia_unit === true);
  const [notes, setNotes] = useState(i.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const subcatsQuery = useQuery({
    queryKey: ['lookups', 'subcategories', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listSubcategories(tenantSlug, { limit: 500 }, signal),
    staleTime: LOOKUP_STALE_MS,
  });
  const specsQuery = useQuery({
    queryKey: ['lookups', 'specialties', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listSpecialties(tenantSlug, { limit: 500 }, signal),
    staleTime: LOOKUP_STALE_MS,
  });
  const countiesQuery = useQuery({
    queryKey: ['lookups', 'service-areas', 'county', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listServiceAreas(
        tenantSlug,
        { area_type: 'county', limit: 500 },
        signal,
      ),
    staleTime: LOOKUP_STALE_MS,
  });

  const categoryOptions = useMemo(
    () => Object.entries(CATEGORY_SLUG_TO_DISPLAY),
    [],
  );

  function handleSave() {
    if (name.trim().length === 0) {
      setError("Enter the organization's name.");
      return;
    }
    if (categorySlug.length === 0) {
      setError('Pick a service category.');
      return;
    }
    setError(null);
    if (!address.isEmpty()) {
      address.pressSave();
      return;
    }
    finishSave(null);
  }

  function finishSave(addressParts: AddressFormParts | null) {
    onSave({
      name: name.trim(),
      category_slug: categorySlug,
      kind: 'other',
      subcategories: subcatSlugs,
      specialties: specSlugs,
      counties: countySlugs,
      phone: nullableTrim(phone),
      phone_2: nullableTrim(phone2),
      fax: nullableTrim(fax),
      address: addressParts,
      website: nullableTrim(website),
      medicaid: medicaid === '' ? null : medicaid,
      dementia_unit: dementiaUnit,
      notes: nullableTrim(notes),
    });
  }

  return (
    <div className="mt-3 p-3 border border-sage-800/[8%] bg-white rounded-md">
      <p className="text-xs font-semibold uppercase tracking-wider text-ink-soft mb-2">
        New organization details
      </p>
      {error && (
        <div role="alert" className="mb-2 p-2 rounded-md bg-warm-50 border border-warm-300 text-xs text-ink">
          {error}
        </div>
      )}
      <div className="space-y-2">
        <SubField label="Organization name" required>
          <TextInput
            type="text"
            nameCase="place"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls()}
            aria-required="true"
          />
        </SubField>
        <SubField label="Service category" required>
          <SelectField size="field"
            value={categorySlug}
            onChange={(e) => setCategorySlug(e.target.value)}
            className="w-full"
            aria-required="true"
          >
            <option value="">Select a category…</option>
            {categoryOptions.map(([slug, display]) => (
              <option key={slug} value={slug}>
                {display}
              </option>
            ))}
          </SelectField>
        </SubField>
        <SubField label="Service types">
          <MultiSelectCheckbox
            options={subcatsQuery.data?.rows ?? []}
            getOptionValue={(r) => r.slug}
            getOptionLabel={(r) => r.display_name}
            selectedValues={subcatSlugs}
            onChange={setSubcatSlugs}
            loading={subcatsQuery.isLoading}
            ariaLabel="Service types"
          />
        </SubField>
        <SubField label="Specialties">
          <MultiSelectCheckbox
            options={specsQuery.data?.rows ?? []}
            getOptionValue={(r) => r.slug}
            getOptionLabel={(r) => r.display_name}
            selectedValues={specSlugs}
            onChange={setSpecSlugs}
            loading={specsQuery.isLoading}
            ariaLabel="Specialties"
          />
        </SubField>
        <SubField label="Counties served">
          <MultiSelectCheckbox
            options={countiesQuery.data?.rows ?? []}
            getOptionValue={(r) => r.slug}
            getOptionLabel={(r) => formatCountyDisplay(r.display_name)}
            selectedValues={countySlugs}
            onChange={setCountySlugs}
            loading={countiesQuery.isLoading}
            ariaLabel="Counties served"
            filterPlaceholder="Filter counties"
          />
        </SubField>
        <SubField label="Phone">
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls()} />
        </SubField>
        <SubField label="Secondary phone">
          <input type="tel" value={phone2} onChange={(e) => setPhone2(e.target.value)} className={inputCls()} />
        </SubField>
        <SubField label="Fax">
          <input type="tel" value={fax} onChange={(e) => setFax(e.target.value)} className={inputCls()} />
        </SubField>
        <AddressFields idPrefix="inline-org" {...address.fields} />
        <AddressCheckPrompt phase={address.check.phase} />
        {address.prompting && (
          <div className="flex flex-wrap justify-end gap-2">
            <AddressCheckChoices
              phase={address.check.phase}
              busy={false}
              onKeep={() => address.choose('checked', false)}
              onUse={() => address.choose('checked', true)}
              onSaveAsTyped={() => address.choose('saved_as_typed', false)}
            />
          </div>
        )}
        <SubField label="Website">
          <input
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://"
            className={inputCls()}
          />
        </SubField>
        <SubField label="Medicaid">
          <SelectField size="field" value={medicaid} onChange={(e) => setMedicaid(e.target.value)} className="w-full">
            <option value="">Unspecified</option>
            {MEDICAID_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </SelectField>
        </SubField>
        <div className="flex items-center gap-2">
          <input
            id="inline-org-dementia"
            type="checkbox"
            checked={dementiaUnit}
            onChange={(e) => setDementiaUnit(e.target.checked)}
            className="w-4 h-4"
          />
          <label htmlFor="inline-org-dementia" className="text-sm text-ink">
            Dementia unit
          </label>
        </div>
        <SubField label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls()} />
        </SubField>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave}>
          Save organization
        </Button>
      </div>
    </div>
  );
}

function SubField({ label, required, children }: { label: string; required?: boolean; children: ReactNode }): ReactNode {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-ink mb-1">
        {label}
        {required && <span className="text-warm-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

function inputCls(): string {
  // Geometry ruling §12: inputs take rounded-base; the standard sage-400 ring.
  return 'w-full min-h-9 px-3 py-1.5 border border-sage-300 rounded-base text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';
}

function nullableTrim(v: string): string | null {
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}
